// Resolve the searcher's origin to privacy-safe geocodes (H3 + short Plus Code)
// plus a display place NAME. Never returns raw coordinates.
//
// Two entry points:
//  - from browser coords (granted geolocation): reverse-geocode to nearest
//    OpenDB place for the display NAME, derive H3/Plus Code at Public tier.
//  - from a typed place name (fallback when geolocation denied/unavailable):
//    forward-lookup the name via OpenDB to coordinates, then derive H3/Plus Code.
//
// In both cases the origin is sent to the candidate search API as `h3Origin`
// (or `place`), never as raw lat/long.

import { nameToCoords, coordsToNearestPlace, deriveGeocode } from '@/lib/geo/places';
import type { Coordinates } from '@/lib/geo/places';
import type { GeocodePrecisionTier } from '@/types/candidate';

export interface SearcherOrigin {
  h3Origin?: string;
  place?: string;
  plusCodeShort?: string;
  label?: string;
}

// The searcher's own origin is always coarsened to Public tier client-side.
const ORIGIN_TIER: GeocodePrecisionTier = 'Public';

export async function resolveSearcherOriginFromCoords(
  latitude: number,
  longitude: number,
): Promise<SearcherOrigin> {
  const coords: Coordinates = { latitude, longitude };
  const geo = deriveGeocode(coords, ORIGIN_TIER);

  let label: string | undefined;
  try {
    const place = await coordsToNearestPlace(latitude, longitude);
    if (place) label = place.name;
  } catch {
    label = undefined;
  }

  return {
    h3Origin: geo.h3Index,
    plusCodeShort: geo.plusCodeShort,
    place: label,
    label,
  };
}

export async function resolveSearcherOriginFromName(
  name: string,
): Promise<SearcherOrigin | null> {
  const named = await nameToCoords(name);
  if (!named) return null;

  const geo = deriveGeocode(
    { latitude: named.latitude, longitude: named.longitude },
    ORIGIN_TIER,
  );

  return {
    h3Origin: geo.h3Index,
    plusCodeShort: geo.plusCodeShort,
    place: named.name,
    label: named.name,
  };
}
